import { useMemo, useState, useEffect } from "react";
import { Modal } from "./TMPanels";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { formatCurrencyExact } from "../lib/romEngine";

/* Generic rate-library picker (used by ROM Section A — Labour and Section C — Equipment).
 *
 * Props:
 *  - open, onClose
 *  - title, subtitle
 *  - items: [{ key, label, rate, rateLabel, category?, alreadyInTable? }]
 *  - onPick(item) — host decides whether to add a new row or bump qty.
 */
export default function RateLibraryPicker({ open, onClose, title, subtitle, items, onPick }) {
    const [search, setSearch] = useState("");
    useEffect(() => { if (open) setSearch(""); }, [open]);

    const grouped = useMemo(() => {
        const map = new Map();
        const q = search.trim().toLowerCase();
        items.forEach((it) => {
            if (q && !it.label.toLowerCase().includes(q)) return;
            const cat = it.category || "All";
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat).push(it);
        });
        return Array.from(map.entries());
    }, [items, search]);

    const total = grouped.reduce((s, [, rows]) => s + rows.length, 0);

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={title}
            subtitle={subtitle}
            wide
            footer={
                <>
                    <div className="text-xs text-[var(--rom-text-2)]">
                        {total} item{total === 1 ? "" : "s"} {search && "match search"}
                    </div>
                    <button onClick={onClose} className="btn btn-ghost" data-testid="rate-library-close">Close</button>
                </>
            }
        >
            <div className="sticky top-0 bg-white pb-3 z-10">
                <div className="relative">
                    <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rom-text-3)]" />
                    <input
                        type="text"
                        placeholder="Search the rate library…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="input pl-9"
                        autoFocus
                        data-testid="rate-library-search"
                    />
                </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-5">
                {grouped.length === 0 && (
                    <div className="text-center py-10 text-[var(--rom-text-3)] text-sm">No items match &ldquo;{search}&rdquo;.</div>
                )}
                {grouped.map(([cat, rows]) => (
                    <div key={cat}>
                        <h4 className="font-display font-bold text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-2)] mb-2 sticky top-0 bg-white py-1 z-10">{cat}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                            {rows.map((it) => (
                                <button
                                    key={it.key}
                                    onClick={() => { onPick(it); }}
                                    data-testid={`rate-library-pick-${it.key}`}
                                    className="text-left p-3 rounded-[4px] border border-[var(--rom-border)] hover:border-[var(--rom-orange)] hover:bg-[var(--rom-orange-soft)]/30 transition-colors flex items-center gap-3"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">{it.label}</div>
                                        <div className="text-[11px] text-[var(--rom-text-3)] uppercase tracking-wider">{it.rateLabel}</div>
                                    </div>
                                    {it.badge && (
                                        <span
                                            className={`chip shrink-0 ${it.badge.tone === "over" ? "chip-orange" : it.badge.tone === "under" ? "chip-green" : "chip-gray"}`}
                                            style={{ padding: "1px 6px" }}
                                            title="Avg invoiced vs quoted on past jobs"
                                            data-testid={`rate-library-badge-${it.key}`}
                                        >
                                            {it.badge.text}
                                        </span>
                                    )}
                                    <div className="font-mono text-sm font-bold tabular shrink-0">
                                        {formatCurrencyExact(it.rate)}
                                    </div>
                                    {it.alreadyInTable && (
                                        <span className="chip chip-orange shrink-0" style={{ padding: "1px 6px" }}>+1 qty</span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </Modal>
    );
}
